import type { ComponentType, SVGProps } from 'react'
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline'
import { ThemePreference, useThemePreference } from '../theme/themePreference'

// Heroicons has no half-filled circle; this matches their 24/outline style.
const HalfCircleIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true" {...props}>
    <circle cx="12" cy="12" r="8.25" />
    <path d="M12 3.75a8.25 8.25 0 0 1 0 16.5Z" fill="currentColor" />
  </svg>
)

const OPTIONS: { value: ThemePreference; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'auto', label: 'Auto', icon: HalfCircleIcon },
]

export const AUTO_HINT = 'Follows your device — switches at sunset if your device does.'

/**
 * Dark / Light / Auto, in Settings → Appearance.
 * Hidden when the dark theme is built out (REACT_APP_TERMINAL_THEME=off).
 */
const ThemeToggle = () => {
  const { preference, setPreference, canChoose } = useThemePreference()
  if (!canChoose) return null
  return (
    <div className="mb-3">
      <div
        role="group"
        aria-label="Theme"
        className="flex rounded-md border border-gray-200 bg-gray-50 p-0.5 dark:border-terminal-border dark:bg-terminal-bg"
      >
        {OPTIONS.map(({ value, label, icon: Icon }) => {
          const selected = preference === value
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => setPreference(value)}
              data-testid={`theme-${value}`}
              className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400 ${
                selected
                  ? 'bg-white text-primary-700 shadow-sm dark:bg-terminal-raised dark:text-primary-400 dark:shadow-none'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {label}
            </button>
          )
        })}
      </div>
      {preference === 'auto' && (
        <p className="mt-1.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400" data-testid="theme-auto-hint">
          {AUTO_HINT}
        </p>
      )}
    </div>
  )
}

export default ThemeToggle
